import {
  Box,
  Heading,
  HStack,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
  VStack
} from "@chakra-ui/react"
import { AvatarCircleIcon } from "@pathwright/pathicons"
import { augmentationSectionFocusTarget } from "@pathwright/ux/src/components/augmentation/sectionFocus"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import Link from "@pathwright/ux/src/components/link/Link"
import HTMLText from "@pathwright/ux/src/components/text/HTMLText"
import RichTextEditorWithMediaManager from "../../media-manager/RichTextEditorWithMediaManager"
import { CoverPageContributorFragment as CoverPageContributor } from "@pathwright/graphql-client/src/generated"
import { ImmersiveTriggerButton } from "../immersive/CoverPageImmersive"
import { COVER_PAGE_INLINE_EDITOR_SOFT_STYLES } from "../ui"
import { useCoverPageTranslate } from "../utils"
import { ContributorCardEditMode } from "./types"
import { getAvatarUserFromContributor, normalizeBio } from "./utils"

const ContributorBio = ({
  bio,
  truncated,
  id,
  maxHeight,
  showImmersiveMore = true
}: {
  bio: string
  maxHeight?: number
  id?: number
  truncated?: boolean
  showImmersiveMore?: boolean
}) => {
  return (
    <HTMLText
      html={bio}
      color="PRIMARY_GRAY"
      w="100%"
      truncate={truncated}
      truncateProps={{
        maxHeight,
        ellipsis: true,
        renderMore: showImmersiveMore
          ? () => <ImmersiveTriggerButton relativeTo={`contributor/${id}`} />
          : undefined
      }}
    />
  )
}

export const ContributorCard = ({
  contributor,
  full,
  isMobile,
  disableTruncation,
  disableNavigation,
  editMode
}: {
  contributor: CoverPageContributor
  full?: boolean
  isMobile?: boolean
  disableTruncation?: boolean
  disableNavigation?: boolean
  editMode?: ContributorCardEditMode | null
}) => {
  const { t } = useCoverPageTranslate()
  const shouldTruncate = !disableTruncation && !editMode?.isExpanded
  const disableCardNavigation = !!disableNavigation || !!editMode

  return (
    <LinkBox asChild>
      <Stack
        className="ContributorCard"
        {...augmentationSectionFocusTarget("surface")}
        direction={full ? "row" : "column"}
        align={"start"}
        gap={full ? "30px" : 2}
        h="100%"
        w="100%"
        maxW={full || isMobile ? "100%" : "200px"}
        key={contributor.id}
        overflow={full ? "hidden" : "visible"}
        px={4}
        pt={full ? 4 : 2}
        pb={full ? 4 : 2}
        cursor={disableCardNavigation ? "default" : "pointer"}
        _hover={
          disableCardNavigation
            ? undefined
            : {
                bgColor: "white",
                borderRadius: "card",
                boxShadow: "card"
              }
        }
      >
        <UserAvatar
          className="ContributorAvatar"
          user={getAvatarUserFromContributor(contributor)}
          size="2xl"
          w="150px"
          maxW="150px"
          h="150px"
          maxH="150px"
          icon={<AvatarCircleIcon style={{ marginRight: "5px" }} />}
        />
        <VStack
          gap={0}
          className="ContributorCardContent"
          w="100%"
          align={"start"}
          h={full && !isMobile ? "150px" : "auto"}
        >
          <VStack gap={0} w="100%" align={"start"}>
            <LinkOverlay asChild>
              <Link
                relativeTo={`contributor/${contributor.id}/`}
                textDecor="none"
                onClick={(event) => {
                  if (disableCardNavigation) {
                    event.preventDefault()
                    event.stopPropagation()
                  }
                }}
              >
                <Heading
                  as="h3"
                  // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
                  size="h4"
                  mt={0}
                  mb={full ? "5px" : 0}
                  color="PRIMARY_GRAY"
                  fontWeight="600"
                  textAlign={"left"}
                >
                  {contributor.salutation ? `${contributor.salutation} ` : ""}
                  {contributor.full_name}
                </Heading>
              </Link>
            </LinkOverlay>
            {contributor.title && (
              <Text
                mt={0}
                mb="5px"
                color="gray.500"
                textAlign={"left"}
                cursor="text"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                }}
              >
                {contributor.title}
              </Text>
            )}
          </VStack>
          {(contributor.bio || editMode) && (
            <Box
              css={{
                "& p:first-of-type": {
                  marginTop: "0"
                },
                "& p:last-of-type": {
                  marginBottom: "0"
                }
              }}
              position="relative"
              w="100%"
              h="100%"
              cursor="text"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
              }}
              textAlign="left"
              maxH={
                editMode?.isExpanded
                  ? "none"
                  : !shouldTruncate
                  ? "auto"
                  : `calc(1.4rem * 4)`
              }
              overflow={
                editMode?.isExpanded
                  ? "visible"
                  : !shouldTruncate
                  ? "visible"
                  : "hidden"
              }
            >
              {editMode ? (
                editMode.isExpanded ? (
                  <Box
                    css={{
                      "& .RichTextEditorControls": {
                        right: 0
                      }
                    }}
                  >
                    <RichTextEditorWithMediaManager
                      value={editMode.value}
                      onChange={editMode.onChange}
                      mode="edit"
                      mediaManagerContextKey={`cover-page-contributor-${contributor.id}`}
                      controlBar={{
                        showEditButton: false,
                        showSaveCancelButtons: false
                      }}
                      editorStyles={COVER_PAGE_INLINE_EDITOR_SOFT_STYLES}
                    />
                  </Box>
                ) : (
                  <Box
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      editMode.onExpand()
                    }}
                    cursor="pointer"
                  >
                    {normalizeBio(editMode.value).trim() ? (
                      <ContributorBio
                        bio={normalizeBio(editMode.value)}
                        truncated={shouldTruncate}
                        maxHeight={!shouldTruncate ? 0 : 1.4 * 16 * 4}
                        id={contributor.id}
                        showImmersiveMore={false}
                      />
                    ) : (
                      <Text color="gray.500">
                        {t("panel_contributors.add_contributor_bio", {
                          defaultValue: "Add contributor bio..."
                        })}
                      </Text>
                    )}
                  </Box>
                )
              ) : (
                <ContributorBio
                  bio={normalizeBio(contributor.bio)}
                  truncated={shouldTruncate}
                  maxHeight={!shouldTruncate ? 0 : 1.4 * 16 * 4}
                  id={contributor.id}
                />
              )}
            </Box>
          )}
        </VStack>
      </Stack>
    </LinkBox>
  )
}

export const ContributorGridCard = ({
  contributor
}: {
  contributor: CoverPageContributor
}) => {
  return (
    <VStack
      className="ContributorGridCard"
      align="stretch"
      borderRadius="card"
      bgColor="backgroundSecondary"
      boxShadow="card"
      overflow="hidden"
      p={"20px"}
      h="100%"
      css={{
        "& .ContributorCard": {
          w: "100%",
          maxW: "100%",
          bgColor: "backgroundSecondary",
          borderRadius: "card",
          boxShadow: "card"
        },
        "& .ContributorAvatar": {
          maxW: "100px",
          maxH: "100px"
        }
      }}
    >
      <HStack h="100px">
        <UserAvatar
          className="ContributorAvatar"
          user={getAvatarUserFromContributor(contributor)}
          size="2xl"
          w="100px"
          maxW="100px"
          h="100px"
          maxH="100px"
          icon={<AvatarCircleIcon style={{ marginRight: "5px" }} />}
        />
        <VStack align="start" gap={0}>
          <Heading
            as="h3"
            // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
            size="h4"
            mt={0}
            mb={0}
            color="PRIMARY_GRAY"
            fontWeight="600"
            textAlign={"left"}
          >
            {contributor.full_name}
          </Heading>
          <Text>{contributor.title}</Text>
        </VStack>
      </HStack>
      <Box w="100%">
        <ContributorBio
          bio={normalizeBio(contributor.bio)}
          truncated
          maxHeight={1.4 * 16 * 6}
          id={contributor.id}
        />
      </Box>
    </VStack>
  )
}
